import Layout from "../Layout";
import { useForm } from "@inertiajs/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { FormButton } from "@/components/ui/form-button";
import { useState, useEffect } from "react";
import { Mail } from "lucide-react";
import { SiGithub } from "@icons-pack/react-simple-icons";
import { FormField } from "@/components/ui/form_field";
import { getFormIconClassName } from "@/lib/utils";

type SigninProps = {
  flash?: {
    alert?: string;
    notice?: string;
  };
  errors?: {
    email?: string[];
    token?: string[];
    oauth?: string[];
  };
};

export default function New({ flash, errors }: SigninProps) {
  const [emailSent, setEmailSent] = useState(false);
  const [sentToEmail, setSentToEmail] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);
  const [canResend, setCanResend] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);

  const magicLinkForm = useForm({
    email: "",
  });

  const resendForm = useForm({
    email: "",
  });

  // Update resend form email when sentToEmail changes
  useEffect(() => {
    if (sentToEmail) {
      resendForm.setData("email", sentToEmail);
    }
  }, [sentToEmail, resendForm]);

  // Countdown timer for resend cooldown
  useEffect(() => {
    if (resendCountdown > 0) {
      const timer = setTimeout(() => {
        setResendCountdown(resendCountdown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendCountdown]);

  function handleMagicLink(e: React.FormEvent) {
    e.preventDefault();
    const email = magicLinkForm.data.email;

    magicLinkForm.post("/magic-link", {
      onSuccess: () => {
        setEmailSent(true);
        setSentToEmail(email);
        setResendCountdown(30);
        setCanResend(false);
        magicLinkForm.reset();
      },
    });
  }

  function handleResend() {
    if (!canResend || resendForm.processing) return;

    setResendError(null); // Clear any previous errors
    resendForm.post("/magic-link", {
      onSuccess: () => {
        setResendCountdown(30);
        setCanResend(false);
        setResendError(null);
      },
      onError: (errors) => {
        // Handle rate limit or other errors
        const errorMessage = errors.email?.[0] || "Failed to resend link. Please try again.";
        setResendError(errorMessage);
      },
    });
  }

  if (emailSent) {
    return (
      <Layout
        title="Check your email"
        description="We've sent you a magic link to sign in to Infralyst."
      >
        <section className="px-4 py-16 pt-40">
          <div className="max-w-4xl mx-auto text-center">
            <div className="max-w-xl mx-auto p-6 sm:p-8 border shadow rounded-3xl bg-background/70 dark:bg-zinc-900/80 backdrop-blur">
              <div className="p-4 rounded-full bg-primary/10 mx-auto w-fit mb-6">
                <Mail className="w-8 h-8 text-primary" aria-hidden="true" />
              </div>
              <h3 className="mb-3 text-2xl font-bold">Check your email</h3>
              <div role="status" aria-live="polite">
                <p className="text-lg mt-3">
                  We sent a sign-in link to <strong>{sentToEmail}</strong>. It expires in 15 minutes.
                </p>
              </div>
              <div className="mt-6 space-y-3">
                <div>
                  <Button
                    type="button"
                    onClick={handleResend}
                    disabled={!canResend || resendForm.processing}
                    className={`w-full ${resendError ? 'border-red-500 dark:border-red-400' : ''}`}
                  >
                    {resendForm.processing
                      ? "Sending..."
                      : resendCountdown > 0
                        ? `Resend in ${resendCountdown}s`
                        : "Resend link"
                    }
                  </Button>
                  {resendError && (
                    <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center">
                      <span className="mr-1">ⓘ</span>
                      {resendError}
                    </p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setEmailSent(false);
                    setSentToEmail("");
                    setResendCountdown(0);
                    setCanResend(false);
                    setResendError(null);
                  }}
                  className="w-full"
                >
                  Use a different email
                </Button>
              </div>
              <p className="text-sm text-surface-foreground/70 mt-3">
                Didn't get it? Check spam/promotions or search for Infralyst.
              </p>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout
      title="Sign In"
      description="Sign in to Infralyst to access your cloud infrastructure analysis and optimization tools."
    >
      <section className="px-4 py-16 pt-40">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="mb-3 text-3xl sm:text-4xl md:text-5xl font-extrabold leading-tight tracking-tight">
            Sign in to <span className="text-primary">Infralyst</span>
          </h1>
          <p className="max-w-2xl mx-auto text-xl leading-relaxed text-surface-foreground/80 mt-3">
            Use GitHub, Google, or your work email.
          </p>

          {flash?.alert && (
            <div className="max-w-xl mx-auto mt-6 mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-red-800 dark:text-red-200">{flash.alert}</p>
            </div>
          )}

          {flash?.notice && (
            <div className="max-w-xl mx-auto mt-6 mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <p className="text-green-800 dark:text-green-200">{flash.notice}</p>
            </div>
          )}

          {(errors?.token || errors?.oauth) && (
            <div className="max-w-xl mx-auto mt-6 mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              {errors.token && <p className="text-red-800 dark:text-red-200">{errors.token[0]}</p>}
              {errors.oauth && <p className="text-red-800 dark:text-red-200">{errors.oauth[0]}</p>}
            </div>
          )}

          <div className="max-w-xl mx-auto mt-8">
            {/* OAuth Sign In */}
            <div className="space-y-4">
              <div className="space-y-3">
                <Button asChild variant="outline" className="w-full py-3 px-4 gap-3">
                  <a href="/auth/github">
                    <SiGithub className="w-5 h-5" />
                    <span className="font-medium">Continue with GitHub</span>
                  </a>
                </Button>

                <Button asChild variant="outline" className="w-full py-3 px-4 gap-3">
                  <a href="/auth/google">
                    <svg className="w-5 h-5" viewBox="0 0 24 24">
                      <path
                        fill="currentColor"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="currentColor"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="currentColor"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                    <span className="font-medium">Continue with Google</span>
                  </a>
                </Button>
              </div>

              {/* Divider */}
              <div className="mt-6">
                <div className="w-full border-t border-border/50" />
              </div>
            </div>

            {/* Magic Link Form */}
            <form
              onSubmit={handleMagicLink}
              className="mt-6 p-8 border shadow rounded-3xl bg-background/70 dark:bg-zinc-900/80 backdrop-blur space-y-6"
            >
              <FormField
                id="email"
                label="Work email"
                error={errors?.email?.[0]}
              >
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={magicLinkForm.data.email}
                  onChange={(e) => magicLinkForm.setData("email", e.target.value)}
                  required
                  placeholder="you@company.com"
                  aria-invalid={!!errors?.email}
                  leftIcon={
                    <Mail
                      className={getFormIconClassName(!!errors?.email)}
                    />
                  }
                />
              </FormField>

              <FormButton
                type="submit"
                isLoading={magicLinkForm.processing}
                loadingText="Sending..."
              >
                Send sign-in link
              </FormButton>

              <p className="text-sm text-surface-foreground/70 text-center">
                Link expires in 15 minutes.
              </p>
            </form>
          </div>
        </div>
      </section>
    </Layout>
  );
}